import {
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import type { StepProps } from '@/components/wizard/StepProps';
import { validateName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ResourceTypes } from '@/data/inventory/resourceTypes';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { useHighFrequencySupportedResourceTypes } from './highFrequencySupportedResourceTypes';
import { pitrPolicySupportedResourceTypes } from './pitrPolicySupportedResourceTypes';

export const BackupPolicyNameSelection = (
  props: StepProps<BackupPolicyEditState>
) => {
  const { highFrequencyBackupPolicy } = useFeatureFlags();
  const highFrequencySupportedResourceTypes =
    useHighFrequencySupportedResourceTypes();
  const next = () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return highFrequencyBackupPolicy ? (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <Stack width='400px' gap='40px'>
        <PolicyNameInput {...props} />
        <PolicyTypeSelection
          selectedType={props.wizardState.policyType}
          setSelectedType={(type) => {
            const resourceTypes =
              type === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
                ? highFrequencySupportedResourceTypes
                : type === BackupPolicyType.BACKUP_POLICY_TYPE_PITR
                  ? pitrPolicySupportedResourceTypes
                  : undefined;
            props.setWizardState({
              ...props.wizardState,
              policyType: type,
              resourceTypes,
            });
          }}
        />
        <Button
          data-testid='create-backup-policy-name-continue-btn'
          variant='contained'
          disabled={!validateName(props.wizardState.name)}
          onClick={next}
          sx={{ height: '50px' }}
        >
          Continue
          <i className='material-symbols-arrow-right-alt ml-[8px]' />
        </Button>
      </Stack>
    </Stack>
  ) : (
    <Stack sx={{ height: '100%' }} alignItems='center' justifyContent='center'>
      <div>
        <Typography variant='h3' sx={{ mb: '24px', textAlign: 'center' }}>
          Please provide a name for this backup policy
        </Typography>
        <OutlinedInput
          inputProps={{ 'data-testid': 'create-backup-policy-name-input' }}
          autoFocus
          sx={{
            width: '655px',
            padding: '8px',
          }}
          placeholder='Use a descriptive name, such as "Production with PII"'
          onChange={(e) =>
            props.setWizardState({ ...props.wizardState, name: e.target.value })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && validateName(props.wizardState.name)) {
              next();
            }
          }}
          value={props.wizardState.name}
          endAdornment={
            <Button
              data-testid='create-backup-policy-name-continue-btn'
              sx={{ width: '200px' }}
              variant='contained'
              disabled={!validateName(props.wizardState.name)}
              onClick={next}
            >
              Continue
              <i className='material-symbols-arrow-right-alt ml-[8px]' />
            </Button>
          }
        />
      </div>
    </Stack>
  );
};

const PolicyNameInput = (props: StepProps<BackupPolicyEditState>) => {
  return (
    <Stack gap='16px'>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Name</FormLabel>
        <OutlinedInput
          inputProps={{ 'data-testid': 'create-backup-policy-name-input' }}
          autoFocus
          sx={{
            padding: '8px',
          }}
          placeholder='Use a descriptive name, such as "Production with PII"'
          onChange={(e) =>
            props.setWizardState({
              ...props.wizardState,
              name: e.target.value,
            })
          }
          value={props.wizardState.name}
        />
      </FormControl>
    </Stack>
  );
};

const usePolicyTypeLabels = (): Partial<
  Record<BackupPolicyType, { label: string; description: string }>
> => {
  const highFrequencySupportedResourceTypes =
    useHighFrequencySupportedResourceTypes();
  return {
    [BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD]: {
      label: 'Standard',
      description:
        'Backup runs up to 4 times a day. Available for all resources.',
    },
    [BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY]: {
      label: 'High frequency',
      description: `Hourly backups supported. Available for ${highFrequencySupportedResourceTypes.map((x) => ResourceTypes[x].displayName).join(' and ')}.`,
    },
    [BackupPolicyType.BACKUP_POLICY_TYPE_PITR]: {
      label: 'Point-in-time recovery',
      description: `PiTR backups supported. Available for ${pitrPolicySupportedResourceTypes.map((x) => ResourceTypes[x].displayName).join(' and ')}.`,
    },
  };
};

const PolicyTypeSelection = ({
  selectedType,
  setSelectedType,
}: {
  selectedType: BackupPolicyType;
  setSelectedType: (type: BackupPolicyType) => void;
}) => {
  const { pitrEnabled } = useFeatureFlags();
  const policyTypeLabels = usePolicyTypeLabels();
  return (
    <Stack gap='16px'>
      <FormControl size='small' className='flex-1'>
        <FormLabel>Type</FormLabel>
        <Select
          sx={{ height: '50px', paddingLeft: '8px' }}
          size='small'
          value={selectedType || BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD}
          onChange={(event) =>
            setSelectedType(event.target.value as BackupPolicyType)
          }
          displayEmpty
          renderValue={(value: BackupPolicyType) => {
            return policyTypeLabels[value]?.label;
          }}
        >
          {Object.values(BackupPolicyType)
            .filter(
              (p) =>
                p !== BackupPolicyType.BACKUP_POLICY_TYPE_UNSPECIFIED &&
                (p !== BackupPolicyType.BACKUP_POLICY_TYPE_PITR || pitrEnabled)
            )
            .map((policyType) => (
              <MenuItem key={policyType} value={policyType}>
                <PolicyTypeOption policyType={policyType as BackupPolicyType} />
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

const PolicyTypeOption = ({ policyType }: { policyType: BackupPolicyType }) => {
  const policyTypeLabels = usePolicyTypeLabels();
  return (
    <Stack gap='4px' padding={'4px'}>
      <Typography variant='body1'>
        {policyTypeLabels[policyType]?.label}
      </Typography>
      <Typography variant='subtitle2' color='text.secondary'>
        {policyTypeLabels[policyType]?.description}
      </Typography>
    </Stack>
  );
};
